"use client";

import * as React from "react";
import { Search, Loader, CircleAlert } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function EcommerceProductsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">
        <Loader className="animate-spin h-6 w-6 mr-2" />
        <span>Searching themes...</span>
      </div>;
  }
  if (error) {
    return <div className="flex items-center justify-center p-8 text-destructive">
        <CircleAlert className="h-6 w-6 mr-2" />
        <span>Unable to load themes. Please try again.</span>
      </div>;
  }
  if (!products.length) {
    return <div className="p-8 text-center text-muted-foreground">
        <p>No themes found matching your search.</p>
        <p className="text-sm mt-1">Try keywords like "Calm", "Bravery", or "Hospital".</p>
      </div>;
  }
  const visibleProducts = products.slice(0, 4);
  return <div className="grid grid-cols-1 gap-2">
      {visibleProducts.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors group text-left" data-index={i}>
          {product.featured_image_url ? <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            </div> : <div className="h-16 w-16 shrink-0 rounded-md bg-secondary flex items-center justify-center text-secondary-foreground/50">
              <Search className="h-6 w-6" />
            </div>}
          <div className="min-w-0 flex-1">
            <h3 className="font-serif font-medium text-foreground group-hover:text-primary transition-colors truncate">
              {product.title}
            </h3>
            <div className="text-sm text-muted-foreground line-clamp-1" dangerouslySetInnerHTML={{
          __html: product.short_description || "Explore this therapeutic theme."
        }} />
          </div>
        </Link>)}
      {products.length > 4 && <div className="pt-2 border-t border-border text-center">
          <span className="text-xs text-muted-foreground">
            Showing 4 of {products.length} results
          </span>
        </div>}
    </div>;
}
export default function DigitalSanctuary() {
  const [searchValue, setSearchValue] = React.useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <section id="digital-sanctuary" className="relative w-full py-24 md:py-32 overflow-hidden bg-gradient-to-b from-background via-secondary/20 to-background">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl mix-blend-multiply animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl mix-blend-multiply" />
      </div>

      <div className="container relative mx-auto px-4 text-center z-10">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-widest text-foreground">
              Digital Sanctuary
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              A safe, guided space to express, tell, and write your life experiences. 
              Discover hope, healing, and agency through our therapeutic video series designed for children and caregivers.
            </p>
          </div>

          <div className="max-w-xl mx-auto relative">
            <EcommerceProductsProvider wp_query={wp_query}>
              <div className="relative group">
                <div className="relative z-20 transition-shadow duration-300 hover:shadow-lg rounded-full">
                  <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search themes (e.g., 'Hospital', 'Calm', 'Bravery')..." className="w-full [&_input]:h-14 [&_input]:pl-6 [&_input]:pr-14 [&_input]:rounded-full [&_input]:border-border/50 [&_input]:bg-background/80 [&_input]:backdrop-blur-sm [&_input]:shadow-sm [&_input]:text-base [&_input]:transition-all [&_input]:focus:ring-ring/50" />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 p-2">
                    <div className="bg-background rounded-full p-1.5 shadow-sm">
                      <Search className="h-5 w-5 text-ring" />
                    </div>
                  </div>
                </div>

                {}
                {hasSearchQuery && <div className="absolute top-full left-0 right-0 mt-4 p-2 bg-background/95 backdrop-blur-md border border-border/50 rounded-2xl shadow-xl z-10 animate-in fade-in zoom-in-95 duration-200">
                    <EcommerceProductsConsumer />
                  </div>}
              </div>
            </EcommerceProductsProvider>
          </div>

          <div className="flex flex-wrap justify-center gap-2 text-sm text-muted-foreground items-center">
            <span>Popular themes:</span>
            <Button variant="link" onClick={() => setSearchValue("Bravery")} className="h-auto p-0 text-muted-foreground hover:text-primary underline decoration-dotted underline-offset-4 transition-colors font-normal">
              Bravery
            </Button>
            <Button variant="link" onClick={() => setSearchValue("Calm")} className="h-auto p-0 text-muted-foreground hover:text-primary underline decoration-dotted underline-offset-4 transition-colors font-normal">
              Calm
            </Button>
            <Button variant="link" onClick={() => setSearchValue("Hospital")} className="h-auto p-0 text-muted-foreground hover:text-primary underline decoration-dotted underline-offset-4 transition-colors font-normal">
              Hospital
            </Button>
          </div>
        </div>
      </div>
    </section>;
}